import React, { useEffect, useRef, useState } from "react";
import { CurrencyInputHelperFun } from "../../../../Constants/utils";
import {
  PO_LINE_QTY_MAX_DIGITS,
  PO_LINE_UNIT_COST_MAX,
  PO_LINE_UNIT_COST_REGISTER_DIGITS,
} from "../../createConstants";
import { clampPoLineQuantity, clampPoLineUnitCost } from "../../createUtils";

const MAX_CURRENCY_DIGITS = 9;

const formatCurrencyAmountDisplay = (value) => {
  const num = Number(value);
  return (Number.isFinite(num) ? num : 0).toFixed(2);
};

const valueToRegisterDigits = (value) => {
  const cents = Math.round((Number(value) || 0) * 100);
  return cents > 0 ? String(cents) : "";
};

const formatRegisterAmount = (
  rawDigits,
  {
    maxValue = null,
    maxDigits = MAX_CURRENCY_DIGITS,
    rejectOverflow = false,
  } = {}
) => {
  let digits = String(rawDigits || "").replace(/[^\d]/g, "");
  if (digits.length > maxDigits) {
    if (rejectOverflow) return null;
    digits = digits.slice(-maxDigits);
  }

  let formatted = CurrencyInputHelperFun(digits);
  let parsed = parseFloat(formatted) || 0;

  if (maxValue != null && parsed > maxValue) {
    parsed = maxValue;
    formatted = maxValue.toFixed(2);
    digits = valueToRegisterDigits(parsed);
  }

  return { formatted, parsed, digits };
};

const currencyInputStyle = {
  height: 32,
  border: "1px solid var(--qv-bd-d0d4dc, #d0d4dc)",
  borderRadius: 6,
  fontSize: 13,
  color: "var(--qv-tx-0f1115, #0f1115)",
  fontFamily: "inherit",
  outline: "none",
  textAlign: "right",
  boxSizing: "border-box",
  fontVariantNumeric: "tabular-nums",
};

/**
 * Register-style currency input (Add Tax / discount / cost price).
 * Digits enter from the right: 1 → 0.01 → 0.10 → 1.00
 */
export const CurrencyAmountInput = ({
  value,
  onChange,
  prefix = "$",
  width = 110,
  disabled = false,
  maxValue = null,
  maxDigits = MAX_CURRENCY_DIGITS,
  rejectOverflow = false,
  hasError = false,
  compact = false,
  inputProps = {},
}) => {
  const [draft, setDraft] = useState(() => formatCurrencyAmountDisplay(value));
  const isFocusedRef = useRef(false);
  const digitsRef = useRef("");
  const inputRef = useRef(null);
  const prefixPad = prefix.length > 1 ? 22 : 18;

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraft(formatCurrencyAmountDisplay(value));
    }
  }, [value]);

  const commitDigits = (nextDigits, inputEl = inputRef.current) => {
    const result = formatRegisterAmount(nextDigits, {
      maxValue,
      maxDigits,
      rejectOverflow,
    });
    if (!result) {
      requestAnimationFrame(() => {
        const len = inputEl?.value?.length ?? 0;
        inputEl?.setSelectionRange(len, len);
      });
      return;
    }
    const { formatted, parsed, digits } = result;
    digitsRef.current = digits;
    setDraft(formatted);
    onChange(parsed);

    requestAnimationFrame(() => {
      const len = formatted.length;
      inputEl?.setSelectionRange(len, len);
    });
  };

  const handleChange = (e) => {
    if (disabled) return;
    commitDigits(e.target.value.replace(/[^\d]/g, ""), e.target);
  };

  const handleFocus = (e) => {
    if (disabled) return;
    isFocusedRef.current = true;
    digitsRef.current = valueToRegisterDigits(value);
    requestAnimationFrame(() => {
      const len = e.target.value.length;
      e.target.setSelectionRange(len, len);
    });
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    digitsRef.current = "";
    setDraft(formatCurrencyAmountDisplay(value));
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Enter") {
      e.target.blur();
      return;
    }

    const { selectionStart, selectionEnd } = e.target;
    const hasSelection = selectionStart !== selectionEnd;

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const nextDigits = hasSelection ? e.key : `${digitsRef.current}${e.key}`;
      commitDigits(nextDigits, e.target);
      return;
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      if (hasSelection) {
        commitDigits("", e.target);
      } else if (e.key === "Backspace") {
        commitDigits(digitsRef.current.slice(0, -1), e.target);
      }
    }
  };

  return (
    <div style={{ width, position: "relative" }}>
      <span
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--qv-tx-7a818f, #7a818f)",
          fontSize: 12,
          pointerEvents: "none",
        }}
      >
        {prefix}
      </span>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={draft}
        disabled={disabled}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...inputProps}
        style={{
          ...currencyInputStyle,
          width: "100%",
          padding: `0 10px 0 ${prefixPad}px`,
          height: compact ? 28 : currencyInputStyle.height,
          fontSize: compact ? 12.5 : currencyInputStyle.fontSize,
          background: disabled ? "var(--qv-bg-f5f6f8, #f5f6f8)" : "var(--qv-bg-ffffff, #fff)",
          borderColor: hasError ? "var(--qv-bd-e03131, #e03131)" : "var(--qv-bd-d0d4dc, #d0d4dc)",
          opacity: disabled ? 0.85 : 1,
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
    </div>
  );
};

/** Unit / cost price column — dollar prefix, register-style entry. */
export const CostInput = ({
  value,
  onChange,
  disabled = false,
  width = 100,
  compact = false,
  inputProps,
}) => (
  <CurrencyAmountInput
    value={value}
    onChange={(v) => onChange(clampPoLineUnitCost(v))}
    prefix="$"
    width={width}
    disabled={disabled}
    compact={compact}
    maxValue={PO_LINE_UNIT_COST_MAX}
    maxDigits={PO_LINE_UNIT_COST_REGISTER_DIGITS}
    rejectOverflow
    inputProps={inputProps}
  />
);

const CurrencyInput = CurrencyAmountInput;

export const NumInput = ({
  value,
  onChange,
  width = 72,
  disabled = false,
  min = 0,
  hasError = false,
  allowEmpty = false,
  compact = false,
  onEnter,
  inputProps = {},
}) => {
  const [draft, setDraft] = useState("");
  const isFocusedRef = useRef(false);

  const formatDisplay = (v) =>
    v === 0 || v === "0" || v == null || v === "" ? "" : String(v);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraft(formatDisplay(value));
    }
  }, [value]);

  const commitDraft = (rawDigits) => {
    if (!rawDigits) {
      if (allowEmpty && min <= 0) {
        onChange("");
        return "";
      }
      const committed = clampPoLineQuantity(0, min);
      onChange(committed);
      return committed;
    }
    const committed = clampPoLineQuantity(rawDigits, min);
    onChange(committed);
    return committed;
  };

  const handleChange = (e) => {
    if (disabled) return;
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, PO_LINE_QTY_MAX_DIGITS);
    setDraft(digits);
    if (!digits) {
      if (allowEmpty && min <= 0) onChange("");
      return;
    }
    const parsed = Number(digits);
    if (parsed >= min) {
      onChange(clampPoLineQuantity(digits, min));
    }
  };

  const handleFocus = () => {
    if (disabled) return;
    isFocusedRef.current = true;
    setDraft(formatDisplay(value));
  };

  const handleBlur = () => {
    if (disabled) return;
    isFocusedRef.current = false;
    const committed = commitDraft(draft);
    setDraft(formatDisplay(committed));
  };

  const { onKeyDown: inputKeyDown, ...restInputProps } = inputProps;

  const handleKeyDown = (e) => {
    inputKeyDown?.(e);
    if (e.defaultPrevented || e.key !== "Enter" || disabled) return;
    e.preventDefault();
    const committed = commitDraft(draft);
    setDraft(formatDisplay(committed));
    onEnter?.(committed);
  };

  const display = isFocusedRef.current ? draft : formatDisplay(value);

  return (
    <input
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder=""
      inputMode="numeric"
      disabled={disabled}
      {...restInputProps}
      style={{
        width,
        height: compact ? 28 : 32,
        padding: compact ? "0 8px" : "0 10px",
        border: `1px solid ${hasError ? "var(--qv-bd-e03131, #e03131)" : "var(--qv-bd-d0d4dc, #d0d4dc)"}`,
        borderRadius: 6,
        fontSize: compact ? 12.5 : 13,
        fontWeight: 600,
        color: "var(--qv-tx-0f1115, #0f1115)",
        fontFamily: "inherit",
        outline: "none",
        textAlign: "center",
        boxSizing: "border-box",
        fontVariantNumeric: "tabular-nums",
        background: disabled ? "var(--qv-bg-f5f6f8, #f5f6f8)" : "var(--qv-bg-ffffff, #fff)",
        opacity: disabled ? 0.85 : 1,
      }}
    />
  );
};

export default CurrencyInput;
